export interface CoinGeckoPrice {
  tron: {
    usd: number;
    usd_24h_change: number;
  };
}

export interface CoinGeckoError {
  error: string;
}

/**
 * Get the appropriate API base URL based on environment
 */
function getApiBaseUrl(): string {
  // In development, use proxy to avoid CORS issues
  if (import.meta.env?.DEV) {
    return '/api/coingecko';
  }
  return 'https://api.coingecko.com';
}

/**
 * Fetch TRX price from CoinGecko API using CORS proxy
 */
export async function fetchTrxPrice(): Promise<number> {
  try {
    const baseUrl = getApiBaseUrl();
    const url = `${baseUrl}/api/v3/simple/price?ids=tron&vs_currencies=usd&include_24hr_change=true`;
    
    console.log(`Fetching TRX price from: ${url}`);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      // Add timeout
      signal: AbortSignal.timeout(10000), // 10 second timeout
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data: CoinGeckoPrice = await response.json();
    
    if (data.tron && data.tron.usd) {
      console.log(`Successfully fetched TRX price: $${data.tron.usd}`);
      return data.tron.usd;
    } else {
      throw new Error('Invalid response format from CoinGecko');
    }
    
  } catch (error) {
    console.error('Failed to fetch TRX price from CoinGecko:', error);
    
    // Return fallback price if API fails
    return 0.12;
  }
}

/**
 * Alternative: Fetch from a public TRX price API that supports CORS
 */
export async function fetchTrxPriceAlternative(): Promise<number> {
  try {
    // Try alternative price sources
    const alternativeUrls = [
      'https://api.binance.com/api/v3/ticker/price?symbol=TRXUSDT',
      'https://api.coinbase.com/v2/prices/TRX-USD/spot',
    ];

    for (const url of alternativeUrls) {
      try {
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          signal: AbortSignal.timeout(5000),
        });

        if (!response.ok) continue;

        const data = await response.json();
        
        if (url.includes('binance') && data.price) {
          return parseFloat(data.price);
        } else if (url.includes('coinbase') && data.data?.amount) {
          return parseFloat(data.data.amount);
        }
      } catch (error) {
        console.warn(`Alternative API failed: ${url}`, error);
        continue;
      }
    }

    throw new Error('All alternative APIs failed');
  } catch (error) {
    console.error('Failed to fetch TRX price from alternative sources:', error);
    return 0.12; // Fallback price
  }
}

/**
 * Get cached TRX price from localStorage
 */
export function getCachedTrxPrice(): number | null {
  try {
    const cached = localStorage.getItem('trx-price-cache');
    if (cached) {
      const data = JSON.parse(cached);
      // Check if cache is less than 1 hour old
      if (Date.now() - data.timestamp < 60 * 60 * 1000) {
        return data.price;
      }
    }
    return null;
  } catch (error) {
    console.error('Failed to read cached TRX price:', error);
    return null;
  }
}

/**
 * Cache TRX price in localStorage
 */
export function cacheTrxPrice(price: number): void {
  try {
    const cacheData = {
      price,
      timestamp: Date.now(),
    };
    localStorage.setItem('trx-price-cache', JSON.stringify(cacheData));
  } catch (error) {
    console.error('Failed to cache TRX price:', error);
  }
}
